import { createFileRoute, Link } from '@tanstack/react-router';
import { useMemo, useState } from 'react';

import type {
  AutoClearResult,
  GlFollowUpStatus,
  GlMatchKeySource,
  GlScheduleItemListRow,
  GlScheduleSnapshot,
  ReconciliationRow,
} from '@dcc-erp/shared';
import { fmt, fmtDate } from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';

import { AppModal } from '../components/AppModal';
import { HelpButton } from '../components/help/HelpButton';
import { PageHead } from '../components/layout';
import { SearchSelect } from '../components/SearchSelect';
import {
  useActiveCompanyId,
  useAutoClearGlSchedule,
  useCertifyGlScheduleSnapshot,
  useCoa,
  useCreateGlScheduleAccount,
  useCreateGlScheduleSnapshot,
  useGlScheduleAccounts,
  useGlScheduleItems,
  useGlScheduleReconciliation,
  useGlScheduleSnapshot,
  useLockGlScheduleSnapshot,
  useMatchGlSchedule,
  usePartialMatchGlSchedule,
  usePeriods,
  useResetGlScheduleMatch,
  useSyncGlSchedule,
  useUpdateGlScheduleAccount,
  useUpdateGlScheduleFollowUp,
  useWriteOffGlSchedule,
} from '../lib/queries';
import {
  downloadWorkbook,
  exportRows,
  todayStamp,
  type CellValue,
  type ExportColumn,
} from '../lib/xlsx';

// =============================================================================
// GL Schedule (Open Item Management) — spec §19 route.
//
// Wires the prototype's "ภาพรวมบัญชี" + "รายละเอียดประกอบบัญชี" tabs to the live
// /companies/:id/gl-schedule endpoints. We deliberately preserve the prototype
// DOM: the rich aging-bucket card, KPI strip, banner, manual-clearing copy,
// and settings table all render identically. Live data drives the item list,
// schedule balance, aging buckets, and the Match/Sync actions.
//
// Display naming follows the prototype: each watched account row keeps a
// `refLabel` (UI hint — e.g. "Advance ID") even though the API only stores
// `description`. We carry refLabel inside a small map below.
// =============================================================================

type TabKey = 'overview' | 'schedule' | 'clearing' | 'settings';
type AcctType = 'Asset' | 'Liability';

interface MonAccount {
  id: string;
  code: string;
  name: string;
  type: AcctType;
  refLabel: string;
  module: string;
  isActive: boolean;
  requireRefKey: boolean;
  matchKeySource: GlMatchKeySource;
  autoClear: boolean;
  toleranceAmount: string | null;
  tolerancePct: string | null;
  writeOffAccountCode: string | null;
}

// UI metadata for the 9 default watched accounts — the API only stores the
// COA code + description, so labels for "Reference Key" and "โมดูล" live
// here for display fidelity with the prototype.
// id/isActive/requireRefKey come from the API and are merged in monAccounts.
type AcctDisplayMeta = Pick<MonAccount, 'name' | 'type' | 'refLabel' | 'module'>;
const ACCT_META: Readonly<Record<string, AcctDisplayMeta>> = {
  '10110005': {
    name: 'เงินทดรองจ่าย — พนักงาน',
    type: 'Asset',
    refLabel: 'Advance ID',
    module: 'เงินทดรองจ่าย',
  },
  '10190001': {
    name: 'ค่าใช้จ่ายจ่ายล่วงหน้า',
    type: 'Asset',
    refLabel: 'Contract ID',
    module: 'ค่าใช้จ่ายล่วงหน้า',
  },
  '10190012': {
    name: 'ภาษีซื้อ (Input VAT)',
    type: 'Asset',
    refLabel: 'Invoice No.',
    module: 'ภาษีมูลค่าเพิ่ม',
  },
  '20150001': {
    name: 'เจ้าหนี้การค้าในประเทศ',
    type: 'Liability',
    refLabel: 'Bill No.',
    module: 'เจ้าหนี้การค้า',
  },
  '20170011': {
    name: 'ภาษีหัก ณ ที่จ่ายค้างจ่าย',
    type: 'Liability',
    refLabel: 'WHT Cert',
    module: 'ภาษีหัก ณ ที่จ่าย',
  },
  '20170036': {
    name: 'ภาษีที่ดินค้างจ่าย',
    type: 'Liability',
    refLabel: 'Tax Ref',
    module: 'ภาษีที่ดิน',
  },
  '20170035': {
    name: 'ภาษีป้ายค้างจ่าย',
    type: 'Liability',
    refLabel: 'Tax Ref',
    module: 'ภาษีป้าย',
  },
  '20170001': {
    name: 'ค่าใช้จ่ายค้างจ่าย',
    type: 'Liability',
    refLabel: 'Branch/Type/Period',
    module: 'ประมาณการค่าใช้จ่าย',
  },
  '20200002': {
    name: 'รายได้รับล่วงหน้า (มัดจำรับ)',
    type: 'Liability',
    refLabel: 'Customer + Project',
    module: 'ลูกหนี้การค้า',
  },
};
const FALLBACK_META: AcctDisplayMeta = {
  name: '(no description)',
  type: 'Asset',
  refLabel: 'Reference',
  module: '—',
};

const AGING_BUCKETS = [
  { label: '0-30 วัน', min: 0, max: 30, color: 'var(--positive)' },
  { label: '31-60 วัน', min: 31, max: 60, color: 'var(--info)' },
  { label: '61-90 วัน', min: 61, max: 90, color: 'var(--warning)' },
  { label: '91-180 วัน', min: 91, max: 180, color: '#c2410c' },
  { label: 'เกิน 180 วัน', min: 181, max: 9999, color: 'var(--negative)' },
] as const;
const FALLBACK_BUCKET = AGING_BUCKETS[0];

function toNumber(v: string | number): number {
  return typeof v === 'number' ? v : Number(v);
}

export const Route = createFileRoute('/glsched')({
  component: GlSchedPage,
});

function GlSchedPage() {
  const companyId = useActiveCompanyId();
  const [tab, setTab] = useState<TabKey>('overview');
  const [agingF, setAgingF] = useState<string>('all');
  const [selectedItemIds, setSelectedItemIds] = useState<Set<string>>(new Set());

  const periodsQuery = usePeriods(companyId, true);
  const periods = periodsQuery.data ?? [];
  const defaultPeriod =
    periods.find((p) => p.status === 'OPEN')?.period ?? periods.at(-1)?.period ?? '';
  const [period, setPeriod] = useState<string>('');
  const activePeriod = period || defaultPeriod;
  const asOf = activePeriod
    ? new Date(Date.UTC(Number(activePeriod.slice(0, 4)), Number(activePeriod.slice(4, 6)), 0))
        .toISOString()
        .slice(0, 10)
    : undefined;
  const activePeriodName = periods.find((p) => p.period === activePeriod)?.nameTh ?? activePeriod;

  const accountsQuery = useGlScheduleAccounts(companyId);
  const accounts = accountsQuery.data;
  // Always-load all OPEN items for KPI strip + overview row totals. The
  // schedule-detail tab re-uses this list and filters by accountCode in JS.
  const itemsQuery = useGlScheduleItems(companyId, { status: 'OPEN', ...(asOf ? { asOf } : {}) });
  const allItems = itemsQuery.data;

  const monAccounts = useMemo<readonly MonAccount[]>(() => {
    // Watched accounts are curated per-company via the settings tab (no auto-seed).
    // Until the API returns (or while the list is empty) we render an empty list
    // and the table shows an empty-state row instead of pretending data exists.
    const rows = accounts ?? [];
    return rows.map((a) => {
      const meta: AcctDisplayMeta = ACCT_META[a.accountCode] ?? {
        ...FALLBACK_META,
        name: a.description,
      };
      return {
        id: a.id,
        code: a.accountCode,
        isActive: a.isActive,
        requireRefKey: a.requireRefKey,
        matchKeySource: a.matchKeySource,
        autoClear: a.autoClear,
        toleranceAmount: a.toleranceAmount != null ? String(a.toleranceAmount) : null,
        tolerancePct: a.tolerancePct != null ? String(a.tolerancePct) : null,
        writeOffAccountCode: a.writeOffAccountCode ?? null,
        ...meta,
      };
    });
  }, [accounts]);

  const [selAcctCode, setSelAcctCode] = useState<string>('');
  const selAcct = monAccounts.find((a) => a.code === selAcctCode) ??
    monAccounts[0] ?? {
      id: '',
      code: '',
      isActive: true,
      requireRefKey: false,
      matchKeySource: 'REF_KEY' as GlMatchKeySource,
      autoClear: false,
      toleranceAmount: null,
      tolerancePct: null,
      writeOffAccountCode: null,
      ...FALLBACK_META,
    };

  // Group items by accountCode once — used by overview totals + schedule view.
  const itemsByAcct = useMemo(() => {
    const m = new Map<string, GlScheduleItemListRow[]>();
    for (const item of allItems ?? []) {
      const list = m.get(item.accountCode) ?? [];
      list.push(item);
      m.set(item.accountCode, list);
    }
    return m;
  }, [allItems]);

  const reconQuery = useGlScheduleReconciliation(companyId, asOf ? { asOf } : {});
  const reconByCode = new Map(reconQuery.data?.map((r) => [r.accountCode, r]));

  const totalOpen = allItems?.length ?? 0;
  const totalOld = (allItems ?? []).filter((i) => i.ageDays > 60).length;
  const assetCount = monAccounts.filter((a) => a.type === 'Asset').length;
  const liabilityCount = monAccounts.filter((a) => a.type === 'Liability').length;
  const reconciled = reconQuery.data?.filter((r) => r.withinTolerance).length ?? 0;

  const tabs: readonly (readonly [TabKey, string])[] = [
    ['overview', 'ภาพรวมบัญชี (Dashboard)'],
    ['schedule', 'รายละเอียดประกอบบัญชี'],
    ['clearing', 'Manual Clearing — จับคู่ปิดรายการ'],
    ['settings', 'ตั้งค่าบัญชีที่ติดตาม'],
  ];

  const syncMut = useSyncGlSchedule(companyId);
  const matchMut = useMatchGlSchedule(companyId);

  // M4: Period-close hooks
  const [closePeriodOpen, setClosePeriodOpen] = useState(false);
  const [closePeriodError, setClosePeriodError] = useState<string | undefined>();
  const snapshotQuery = useGlScheduleSnapshot(companyId, activePeriod);
  const activeSnapshot = snapshotQuery.data ?? null;

  const createSnapshotMut = useCreateGlScheduleSnapshot(companyId);
  const certifySnapshotMut = useCertifyGlScheduleSnapshot(companyId);
  const lockSnapshotMut = useLockGlScheduleSnapshot(companyId);
  const closeBusy =
    createSnapshotMut.isPending || certifySnapshotMut.isPending || lockSnapshotMut.isPending;

  const snapshotStatusBadge =
    activeSnapshot?.status === 'LOCKED'
      ? 'งวดนี้: ล็อกแล้ว 🔒'
      : activeSnapshot?.status === 'CERTIFIED'
        ? 'งวดนี้: รับรองแล้ว ✓'
        : activeSnapshot?.status === 'DRAFT'
          ? 'งวดนี้: DRAFT'
          : null;

  // Recon array for close-period modal (derived from reconQuery)
  const reconArray = reconQuery.data ?? [];

  return (
    <div className="page">
      <PageHead
        crumbs={['บัญชีแยกประเภท', 'กระทบยอด GL & รายละเอียดประกอบบัญชี']}
        title="กระทบยอด GL & รายละเอียดประกอบบัญชี (Open Item Management)"
        actions={
          <>
            <HelpButton guide="glsched" />
            <select
              className="select"
              value={activePeriod}
              onChange={(e) => setPeriod(e.target.value)}
            >
              {periods.map((p) => (
                <option key={p.period} value={p.period}>
                  งวด: {p.nameTh}
                </option>
              ))}
            </select>
            {snapshotStatusBadge && (
              <span
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  padding: '4px 10px',
                  borderRadius: 12,
                  background:
                    activeSnapshot?.status === 'LOCKED'
                      ? '#f0fdf4'
                      : activeSnapshot?.status === 'CERTIFIED'
                        ? '#eff6ff'
                        : '#fffbeb',
                  color:
                    activeSnapshot?.status === 'LOCKED'
                      ? 'var(--positive)'
                      : activeSnapshot?.status === 'CERTIFIED'
                        ? '#1a56a6'
                        : '#92400e',
                  border:
                    activeSnapshot?.status === 'LOCKED'
                      ? '1px solid #86efac'
                      : activeSnapshot?.status === 'CERTIFIED'
                        ? '1px solid #93c5fd'
                        : '1px solid #fcd34d',
                  whiteSpace: 'nowrap',
                }}
                data-testid="snapshot-badge"
              >
                {snapshotStatusBadge}
              </span>
            )}
            <button
              className="btn"
              onClick={() => syncMut.mutate({})}
              disabled={syncMut.isPending || !companyId}
              title="ดึง JV ใหม่ ๆ เข้า GL Schedule (idempotent)"
            >
              <Icon name="refresh" size={14} />{' '}
              {syncMut.isPending ? 'กำลัง Sync...' : 'Sync รายการใหม่'}
            </button>
            <button
              className="btn"
              disabled={!companyId || !activePeriod}
              onClick={() => void handleExportPack()}
              title="Export ทุกบัญชีเป็น workbook หลายชีต"
            >
              <Icon name="download" size={14} /> Export ประกอบบัญชี (ZIP)
            </button>
            <button
              className="btn primary"
              disabled={!companyId || !activePeriod}
              onClick={() => {
                setClosePeriodError(undefined);
                setClosePeriodOpen(true);
              }}
            >
              <Icon name="check" size={14} /> ปิดงวด & Lock
            </button>
          </>
        }
      />

      <div className="kpi-grid">
        <div className="kpi">
          <div className="label">
            <span className="dot" />
            บัญชีที่ติดตาม (Monitored)
          </div>
          <div className="value tnum">
            {monAccounts.length} <span className="unit">บัญชี</span>
          </div>
          <div className="trend faint">
            {assetCount} Asset · {liabilityCount} Liability
          </div>
        </div>
        <div className="kpi">
          <div className="label">
            <span className="dot" style={{ background: 'var(--positive)' }} />
            กระทบยอดแล้ว (GL = Open Items)
          </div>
          <div
            className="value tnum"
            style={{
              color:
                reconciled === monAccounts.length && monAccounts.length > 0
                  ? 'var(--positive)'
                  : 'var(--warning)',
            }}
          >
            {reconciled} <span className="unit">/ {monAccounts.length}</span>
          </div>
          <div
            className={
              reconciled === monAccounts.length && monAccounts.length > 0
                ? 'trend pos'
                : 'trend faint'
            }
          >
            {reconciled === monAccounts.length && monAccounts.length > 0 ? (
              <>
                <Icon name="check" size={11} /> 100% ของบัญชีที่ติดตาม
              </>
            ) : (
              `${monAccounts.length - reconciled} บัญชียังมีผลต่าง`
            )}
          </div>
        </div>
        <div className="kpi">
          <div className="label">
            <span className="dot" style={{ background: 'var(--warning)' }} />
            Open Items รวม
          </div>
          <div className="value tnum">
            {totalOpen} <span className="unit">รายการ</span>
          </div>
          <div className="trend faint">รอจับคู่ปิด (Clearing)</div>
        </div>
        <div className="kpi">
          <div className="label">
            <span className="dot" style={{ background: 'var(--negative)' }} />
            Open Items ค้างนาน &gt;60 วัน
          </div>
          <div className="value tnum">
            {totalOld} <span className="unit">รายการ</span>
          </div>
          <div className="trend down">ควรติดตาม</div>
        </div>
      </div>

      <div
        style={{
          background: '#fafafa',
          border: '1px solid var(--border)',
          borderRadius: 8,
          padding: '14px 18px',
          marginBottom: 'var(--section-gap)',
        }}
      >
        <div className="section-title" style={{ margin: '0 0 10px' }}>
          หลักการทำงาน — Open Item Management
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12 }}>
          <BannerStep
            n={1}
            color="#1a56a6"
            title="Reference Key บังคับต่อบัญชี"
            desc='เปิด-ปิด "บังคับใส่ Ref" ต่อบัญชีได้ในแท็บตั้งค่า — บัญชีที่เปิดจะบล็อก JV ที่ไม่มี Reference'
          />
          <BannerStep
            n={2}
            color="#117a3d"
            title="Auto-clearing เมื่อ matchKey ตรงกัน"
            desc="ระบบจับคู่ปิดกลุ่มที่ผลรวม = 0 อัตโนมัติ (ตั้งค่าได้รายบัญชี) · ที่เหลือเป็น worklist"
          />
          <BannerStep
            n={3}
            color="#b54708"
            title="Schedule = ผลรวม Open Items"
            desc="ระบบกระทบยอด GL กับ Open Items อัตโนมัติ · ผลต่างที่ไม่เป็น 0 จะถูกตั้งสถานะให้ตรวจสอบ"
          />
        </div>
      </div>

      <div className="page-tabs">
        {tabs.map(([k, l]) => (
          <div key={k} className={`pt ${tab === k ? 'active' : ''}`} onClick={() => setTab(k)}>
            {l}
          </div>
        ))}
      </div>

      {tab === 'overview' && (
        <GLOverview
          accounts={monAccounts}
          itemsByAcct={itemsByAcct}
          onPick={(a) => {
            setSelAcctCode(a.code);
            setTab('schedule');
          }}
          isPending={accountsQuery.isPending}
          error={accountsQuery.error}
          activePeriodName={activePeriodName}
          reconByCode={reconByCode}
        />
      )}
      {tab === 'schedule' && (
        <GLScheduleDetail
          acct={selAcct}
          accounts={monAccounts}
          setAcct={(a) => setSelAcctCode(a.code)}
          agingF={agingF}
          setAgingF={setAgingF}
          companyId={companyId}
          selectedItemIds={selectedItemIds}
          setSelectedItemIds={setSelectedItemIds}
          onMatch={async (ids, notes) => {
            await matchMut.mutateAsync({ itemIds: ids, ...(notes ? { notes } : {}) });
            setSelectedItemIds(new Set());
          }}
          matchPending={matchMut.isPending}
          {...(asOf ? { asOf } : {})}
          activePeriodName={activePeriodName}
          reconByCode={reconByCode}
        />
      )}
      {tab === 'clearing' && <ClearingPage companyId={companyId} />}
      {tab === 'settings' && <GLSettings accounts={monAccounts} companyId={companyId} />}

      {closePeriodOpen && (
        <ClosePeriodModal
          period={activePeriod}
          periodName={activePeriodName}
          recon={reconArray}
          snapshot={activeSnapshot}
          busy={closeBusy}
          error={closePeriodError}
          onClose={() => setClosePeriodOpen(false)}
          onPrepare={() => {
            setClosePeriodError(undefined);
            createSnapshotMut.mutate(
              { period: activePeriod },
              {
                onError: (err) =>
                  setClosePeriodError(err instanceof Error ? err.message : String(err)),
              },
            );
          }}
          onCertify={() => {
            setClosePeriodError(undefined);
            certifySnapshotMut.mutate(activePeriod, {
              onError: (err) => {
                const msg = err instanceof Error ? err.message : String(err);
                setClosePeriodError(
                  msg.includes('400') || msg.toLowerCase().includes('preparer')
                    ? 'ผู้รับรองต้องไม่ใช่ผู้จัดทำ'
                    : msg,
                );
              },
            });
          }}
          onLock={() => {
            setClosePeriodError(undefined);
            lockSnapshotMut.mutate(activePeriod, {
              onError: (err) =>
                setClosePeriodError(err instanceof Error ? err.message : String(err)),
            });
          }}
        />
      )}
    </div>
  );

  // Export pack — builds multi-sheet workbook from all monitored accounts.
  // Declared inside the component so it can close over itemsByAcct + reconByCode + activePeriod.
  async function handleExportPack() {
    await buildAndDownloadExportPack({ itemsByAcct, reconByCode, monAccounts, activePeriod });
  }
}

// =============================================================================
// buildAndDownloadExportPack — multi-sheet workbook (one sheet per account).
// Exported for unit testing (glsched.m4.test.tsx).
// =============================================================================
export async function buildAndDownloadExportPack({
  itemsByAcct,
  reconByCode,
  monAccounts,
  activePeriod,
}: {
  itemsByAcct: Map<string, GlScheduleItemListRow[]>;
  reconByCode: Map<string, ReconciliationRow>;
  monAccounts: readonly MonAccount[];
  activePeriod: string;
}): Promise<void> {
  // Summary sheet: per-account glBalance / scheduleOpenSum / delta / openCount / oldCount
  const summaryHeader: CellValue[] = [
    'รหัสบัญชี',
    'ชื่อบัญชี',
    'ยอด GL คงเหลือ',
    'ผลรวม Open Items',
    'ผลต่าง (Delta)',
    'Open Count',
    'ค้าง >60 วัน',
  ];
  const summaryRows: CellValue[][] = monAccounts.map((a) => {
    const r = reconByCode.get(a.code);
    const items = itemsByAcct.get(a.code) ?? [];
    return [
      a.code,
      a.name,
      r ? Number(r.glBalance) : items.reduce((s, i) => s + toNumber(i.amount), 0),
      r ? Number(r.scheduleOpenSum) : items.reduce((s, i) => s + toNumber(i.amount), 0),
      r ? Number(r.delta) : 0,
      r ? r.openCount : items.length,
      r ? r.oldCount : items.filter((i) => i.ageDays > 60).length,
    ];
  });

  const sheets: { name: string; aoa: CellValue[][] }[] = [
    { name: 'สรุป', aoa: [summaryHeader, ...summaryRows] },
  ];

  // Per-account detail sheets
  const itemHeader: CellValue[] = [
    'Ref',
    'วันที่',
    'คำอธิบาย',
    'ยอดคงค้าง',
    'อายุ',
    'matchKey',
    'followUpStatus',
  ];
  for (const acct of monAccounts) {
    const items = itemsByAcct.get(acct.code) ?? [];
    const rows: CellValue[][] = items.map((it) => [
      it.refKey ?? '',
      it.voucherDate ? String(it.voucherDate).slice(0, 10) : '',
      it.description ?? it.party ?? '',
      Number(it.amount),
      it.ageDays,
      it.matchKey ?? '',
      it.followUpStatus ?? '',
    ]);
    sheets.push({ name: acct.code, aoa: [itemHeader, ...rows] });
  }

  await downloadWorkbook(`ประกอบบัญชี_${activePeriod}.xlsx`, sheets);
}

// =============================================================================
// FollowUpCell — per-item follow-up status selector + aged warning chip.
// Exported for unit testing (glsched.m3.test.tsx).
// =============================================================================
export function FollowUpCell({
  item,
  onChange,
}: {
  item: Pick<GlScheduleItemListRow, 'followUpStatus' | 'ageDays'>;
  onChange: (status: GlFollowUpStatus) => void;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
      <select
        className="select"
        style={{ height: 26, fontSize: 12, minWidth: 110 }}
        aria-label="Follow-up Status"
        value={item.followUpStatus ?? 'NONE'}
        onChange={(e) => onChange(e.target.value as GlFollowUpStatus)}
      >
        <option value="NONE">ไม่ระบุ</option>
        <option value="IN_PROGRESS">กำลังติดตาม</option>
        <option value="ESCALATED">ยกระดับ</option>
        <option value="RESOLVED">แก้ไขแล้ว</option>
      </select>
      {item.ageDays > 60 && (
        <span
          className="chip"
          style={{
            background: 'var(--warning-tint, #fff7ed)',
            color: '#b45309',
            fontSize: 11,
            padding: '1px 6px',
            borderRadius: 10,
            border: '1px solid #fbbf24',
            whiteSpace: 'nowrap',
          }}
        >
          ติดตาม
        </span>
      )}
    </div>
  );
}

// =============================================================================
// WriteOffModal — residual write-off confirmation modal.
// Exported for unit testing (glsched.m3.test.tsx).
// =============================================================================
export function WriteOffModal({
  residual,
  onConfirm,
  onClose,
  error,
}: {
  residual: number;
  onConfirm: (reasonCode: string) => void;
  onClose: () => void;
  error?: string | undefined;
}) {
  const [reason, setReason] = useState('');
  return (
    <AppModal
      title="ตัดจำหน่ายส่วนต่าง (Write-off)"
      subtitle="ส่วนต่างที่อยู่ในวงเงิน Tolerance — ระบบจะบันทึก JV ตัดจำหน่ายอัตโนมัติ"
      onClose={onClose}
      footer={
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn ghost" onClick={onClose}>
            ยกเลิก
          </button>
          <button
            className="btn primary"
            disabled={!reason.trim()}
            onClick={() => onConfirm(reason.trim())}
          >
            ยืนยันตัดจำหน่าย
          </button>
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, padding: '4px 0' }}>
        <div>
          <div className="meta" style={{ marginBottom: 4 }}>
            ยอดส่วนต่างที่จะตัดจำหน่าย (Residual)
          </div>
          <div
            style={{
              fontSize: 22,
              fontWeight: 700,
              color: residual < 0 ? 'var(--negative)' : 'var(--warning)',
              fontFamily: 'var(--font-display)',
            }}
            className="tnum"
          >
            {fmt(residual)}
          </div>
        </div>
        <div>
          <label style={{ display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 6 }}>
            เหตุผล / Reason Code <span style={{ color: 'var(--negative)' }}>*</span>
          </label>
          <input
            className="input"
            style={{ width: '100%' }}
            placeholder="ระบุเหตุผลการตัดจำหน่าย (จำเป็น)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            aria-label="Reason Code"
          />
        </div>
        {error && (
          <div
            style={{
              color: 'var(--negative)',
              background: '#fef2f2',
              border: '1px solid #fca5a5',
              borderRadius: 6,
              padding: '8px 12px',
              fontSize: 13,
            }}
          >
            {error}
          </div>
        )}
      </div>
    </AppModal>
  );
}

// =============================================================================
// ClosePeriodModal — period-close (prepare snapshot → certify → lock) flow.
// Exported for unit testing (glsched.m4.test.tsx).
// =============================================================================
export interface ClosePeriodModalProps {
  period: string;
  periodName: string;
  /** All reconciliation rows for this period (used to derive summary). */
  recon: ReconciliationRow[];
  snapshot: GlScheduleSnapshot | null | undefined;
  onPrepare: () => void;
  onCertify: () => void;
  onLock: () => void;
  onClose: () => void;
  busy?: boolean | undefined;
  error?: string | undefined;
}

export function ClosePeriodModal({
  period,
  periodName,
  recon,
  snapshot,
  onPrepare,
  onCertify,
  onLock,
  onClose,
  busy,
  error,
}: ClosePeriodModalProps) {
  const reconciledCount = recon.filter((r) => r.withinTolerance).length;
  const totalAccounts = recon.length;
  const totalDelta = recon.reduce((s, r) => s + Math.abs(Number(r.delta)), 0);
  const allReconciled = reconciledCount === totalAccounts && totalAccounts > 0;

  const statusLabel =
    snapshot == null
      ? 'ยังไม่ได้จัดทำ'
      : snapshot.status === 'DRAFT'
        ? 'DRAFT — รอรับรอง'
        : snapshot.status === 'CERTIFIED'
          ? 'CERTIFIED — รอล็อก'
          : 'LOCKED — ล็อกแล้ว';

  const statusColor =
    snapshot == null
      ? 'var(--text-muted)'
      : snapshot.status === 'DRAFT'
        ? 'var(--warning)'
        : snapshot.status === 'CERTIFIED'
          ? 'var(--info, #1a56a6)'
          : 'var(--positive)';

  const isLocked = snapshot?.status === 'LOCKED';
  const isDraft = snapshot?.status === 'DRAFT';
  const isCertified = snapshot?.status === 'CERTIFIED';

  return (
    <AppModal
      title={`ปิดงวด & Lock — งวด ${periodName}`}
      subtitle={`งวด ${period} · ดำเนินการตาม 3 ขั้นตอน: จัดทำ → รับรอง → ล็อก`}
      onClose={onClose}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 18, padding: '4px 0' }}>
        {/* Reconciliation summary */}
        <div
          style={{
            background: '#fafafa',
            border: '1px solid var(--border)',
            borderRadius: 8,
            padding: '14px 16px',
          }}
        >
          <div className="section-title" style={{ margin: '0 0 10px', fontSize: 13 }}>
            สรุปการกระทบยอด — งวด {periodName}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12 }}>
            <div>
              <div className="meta">บัญชีกระทบยอดแล้ว</div>
              <div
                className="tnum"
                style={{
                  fontSize: 22,
                  fontWeight: 700,
                  color: allReconciled ? 'var(--positive)' : 'var(--warning)',
                }}
              >
                {reconciledCount}{' '}
                <span style={{ fontSize: 15, fontWeight: 400 }}>/ {totalAccounts}</span>
              </div>
            </div>
            <div>
              <div className="meta">ผลรวม |Delta| ทั้งหมด</div>
              <div
                className="tnum"
                style={{
                  fontSize: 22,
                  fontWeight: 700,
                  color: totalDelta === 0 ? 'var(--positive)' : 'var(--negative)',
                }}
              >
                {fmt(totalDelta)}
              </div>
            </div>
            <div>
              <div className="meta">สถานะ Snapshot</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: statusColor }}>{statusLabel}</div>
              {snapshot && (
                <div className="meta" style={{ marginTop: 3, fontSize: 12 }}>
                  จัดทำโดย: {snapshot.preparedBy}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Warning when not all reconciled */}
        {!allReconciled && (
          <div
            style={{
              background: '#fffbeb',
              border: '1px solid #fcd34d',
              borderRadius: 6,
              padding: '10px 14px',
              fontSize: 14,
              color: '#92400e',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <span style={{ fontSize: 16 }}>⚠️</span>
            มีบัญชีที่ยังไม่กระทบยอด — ควรแก้ก่อนปิดงวด (แต่ไม่บล็อก ผู้รับรองเป็นตัวควบคุม)
          </div>
        )}

        {/* 3-step action row */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div className="section-title" style={{ margin: 0, fontSize: 13 }}>
            ขั้นตอนการปิดงวด
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10 }}>
            {/* Step 1: Prepare snapshot */}
            <div
              style={{
                border: '1px solid var(--border)',
                borderRadius: 8,
                padding: '14px',
                background: 'white',
              }}
            >
              <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>① จัดทำ Snapshot</div>
              <div className="meta" style={{ marginBottom: 10 }}>
                บันทึกยอด ณ สิ้นงวด (ทำซ้ำได้จนกว่าจะ Certify)
              </div>
              <button
                className="btn primary"
                style={{ width: '100%' }}
                disabled={!!busy || isLocked}
                onClick={onPrepare}
                aria-label="จัดทำ Snapshot"
              >
                {busy ? 'กำลังดำเนินการ...' : 'จัดทำ Snapshot'}
              </button>
            </div>

            {/* Step 2: Certify */}
            <div
              style={{
                border: `1px solid ${isDraft ? 'var(--brand)' : 'var(--border)'}`,
                borderRadius: 8,
                padding: '14px',
                background: isDraft ? 'var(--brand-tint, #eff6ff)' : 'white',
              }}
            >
              <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>
                ② รับรอง (Certify)
              </div>
              <div className="meta" style={{ marginBottom: 10 }}>
                ต้องไม่ใช่คนเดียวกับผู้จัดทำ (4-eyes SoD)
              </div>
              <button
                className="btn"
                style={{ width: '100%' }}
                disabled={!!busy || !isDraft}
                onClick={onCertify}
                aria-label="รับรอง Certify"
                title={!isDraft ? 'ต้องมี Snapshot สถานะ DRAFT ก่อน' : undefined}
              >
                {busy ? 'กำลังดำเนินการ...' : 'รับรอง (Certify)'}
              </button>
            </div>

            {/* Step 3: Lock */}
            <div
              style={{
                border: `1px solid ${isCertified ? 'var(--positive)' : 'var(--border)'}`,
                borderRadius: 8,
                padding: '14px',
                background: isCertified ? '#f0fdf4' : 'white',
              }}
            >
              <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>③ ล็อกงวด (Lock)</div>
              <div className="meta" style={{ marginBottom: 10 }}>
                ล็อกถาวร — ไม่สามารถแก้ไขได้อีก
              </div>
              <button
                className="btn"
                style={{
                  width: '100%',
                  color: isCertified ? 'white' : undefined,
                  background: isCertified ? 'var(--positive)' : undefined,
                }}
                disabled={!!busy || !isCertified}
                onClick={onLock}
                aria-label="ล็อกงวด Lock"
                title={!isCertified ? 'ต้องมี Snapshot สถานะ CERTIFIED ก่อน' : undefined}
              >
                {busy ? 'กำลังดำเนินการ...' : isLocked ? 'ล็อกแล้ว 🔒' : 'ล็อกงวด (Lock)'}
              </button>
            </div>
          </div>
        </div>

        {/* Server error */}
        {error && (
          <div
            style={{
              color: 'var(--negative)',
              background: '#fef2f2',
              border: '1px solid #fca5a5',
              borderRadius: 6,
              padding: '10px 14px',
              fontSize: 13,
            }}
          >
            {error}
          </div>
        )}
      </div>
    </AppModal>
  );
}

// =============================================================================
// ReconBadge — displays the real independent GL tie-out result per account.
// Exported for unit testing (glsched.recon.test.tsx).
// =============================================================================
export function ReconBadge({
  delta,
  withinTolerance,
}: {
  delta: string;
  withinTolerance: boolean;
}) {
  if (withinTolerance) {
    return (
      <span className="badge positive">
        <Icon name="check" size={10} /> ตรงกับ Schedule
      </span>
    );
  }
  return (
    <span className="badge negative">
      <span className="bdot" /> ผลต่าง <span>{delta}</span>
    </span>
  );
}

function BannerStep({
  n,
  color,
  title,
  desc,
}: {
  n: number;
  color: string;
  title: string;
  desc: string;
}) {
  return (
    <div
      style={{
        display: 'flex',
        gap: 10,
        padding: '10px',
        background: 'white',
        borderRadius: 6,
        border: '1px solid var(--border)',
      }}
    >
      <div
        style={{
          width: 28,
          height: 28,
          borderRadius: '50%',
          background: color,
          color: 'white',
          display: 'grid',
          placeItems: 'center',
          fontWeight: 700,
          fontSize: 14,
          fontFamily: 'var(--font-display)',
          flexShrink: 0,
        }}
      >
        {n}
      </div>
      <div>
        <div style={{ fontWeight: 600, fontSize: 14 }}>{title}</div>
        <div className="meta" style={{ marginTop: 3 }}>
          {desc}
        </div>
      </div>
    </div>
  );
}

interface OverviewProps {
  accounts: readonly MonAccount[];
  itemsByAcct: Map<string, GlScheduleItemListRow[]>;
  onPick: (a: MonAccount) => void;
  isPending: boolean;
  error: Error | null;
  activePeriodName: string;
  reconByCode: Map<string, ReconciliationRow>;
}

function GLOverview({
  accounts,
  itemsByAcct,
  onPick,
  isPending,
  error,
  activePeriodName,
  reconByCode,
}: OverviewProps) {
  const [typeFilter, setTypeFilter] = useState<'' | 'Asset' | 'Liability'>('');
  const [statusFilter, setStatusFilter] = useState<
    '' | 'กระทบยอดแล้ว' | 'ยังไม่กระทบยอด' | 'มีรายการค้างนาน'
  >('');

  const filteredAccounts = useMemo(() => {
    let rows = accounts;
    if (typeFilter) rows = rows.filter((a) => a.type === typeFilter);
    if (statusFilter) {
      rows = rows.filter((a) => {
        const r = reconByCode.get(a.code);
        if (statusFilter === 'กระทบยอดแล้ว') return r?.withinTolerance === true;
        if (statusFilter === 'มีรายการค้างนาน') return (r?.oldCount ?? 0) > 0;
        // "ยังไม่กระทบยอด" — recon row exists but not within tolerance, or no recon row
        return !r?.withinTolerance;
      });
    }
    return rows;
  }, [accounts, typeFilter, statusFilter, reconByCode]);

  async function handleExportAll() {
    interface OverviewRow {
      code: string;
      name: string;
      glBalance: string | number;
      openCount: number;
      oldCount: number;
      delta: string | number;
    }
    const exportData: OverviewRow[] = accounts.map((a) => {
      const r = reconByCode.get(a.code);
      const items = itemsByAcct.get(a.code) ?? [];
      return {
        code: a.code,
        name: a.name,
        glBalance: r ? r.glBalance : items.reduce((s, i) => s + toNumber(i.amount), 0),
        openCount: r ? r.openCount : items.length,
        oldCount: r ? r.oldCount : items.filter((i) => i.ageDays > 60).length,
        delta: r ? r.delta : 0,
      };
    });
    const cols: ExportColumn<OverviewRow>[] = [
      { header: 'รหัสบัญชี', value: (r) => r.code, width: 14 },
      { header: 'ชื่อบัญชี', value: (r) => r.name, width: 36 },
      { header: 'ยอด GL คงเหลือ', value: (r) => Number(r.glBalance), width: 18 },
      { header: 'Open Items', value: (r) => r.openCount, width: 12 },
      { header: 'ค้าง >60 วัน', value: (r) => r.oldCount, width: 12 },
      { header: 'ผลต่าง (Delta)', value: (r) => Number(r.delta), width: 18 },
    ];
    await exportRows(`ภาพรวม_ประกอบบัญชี_${todayStamp()}.xlsx`, 'ภาพรวม', cols, exportData);
  }

  return (
    <div className="tbl-shell">
      <div className="filter-row">
        <span style={{ fontSize: 14, fontWeight: 600 }}>
          บัญชีที่ต้องทำรายละเอียดประกอบ{activePeriodName ? ` — ปิดงวด ${activePeriodName}` : ''}
        </span>
        <select
          className="select"
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value as '' | 'Asset' | 'Liability')}
        >
          <option value="">ประเภท: ทั้งหมด</option>
          <option value="Asset">Asset</option>
          <option value="Liability">Liability</option>
        </select>
        <select
          className="select"
          value={statusFilter}
          onChange={(e) =>
            setStatusFilter(
              e.target.value as '' | 'กระทบยอดแล้ว' | 'ยังไม่กระทบยอด' | 'มีรายการค้างนาน',
            )
          }
        >
          <option value="">สถานะ: ทั้งหมด</option>
          <option value="กระทบยอดแล้ว">กระทบยอดแล้ว</option>
          <option value="ยังไม่กระทบยอด">ยังไม่กระทบยอด</option>
          <option value="มีรายการค้างนาน">มีรายการค้างนาน</option>
        </select>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
          <button className="btn sm" onClick={() => void handleExportAll()}>
            <Icon name="download" size={12} /> Export ทั้งหมด
          </button>
        </div>
      </div>
      <div className="tbl-wrap" style={{ maxHeight: 600 }}>
        <table className="tbl">
          <thead>
            <tr>
              <th style={{ width: 24 }}>
                <input type="checkbox" />
              </th>
              <th style={{ width: 80 }}>รหัส</th>
              <th>ชื่อบัญชี</th>
              <th>ประเภท</th>
              <th>โมดูลที่เกี่ยวข้อง</th>
              <th>Reference Key</th>
              <th className="num">ยอด GL คงเหลือ</th>
              <th className="num">Open Items</th>
              <th className="num">ค้าง &gt;60 วัน</th>
              <th className="num">อายุเฉลี่ย</th>
              <th>กระทบยอด</th>
              <th style={{ width: 130 }}>การกระทำ</th>
            </tr>
          </thead>
          <tbody>
            {isPending ? (
              <tr>
                <td colSpan={12} className="muted" style={{ padding: 24, textAlign: 'center' }}>
                  กำลังโหลด…
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td
                  colSpan={12}
                  className="muted"
                  style={{ padding: 24, textAlign: 'center', color: 'var(--negative)' }}
                >
                  โหลดล้มเหลว: {error.message}
                </td>
              </tr>
            ) : accounts.length === 0 ? (
              <tr>
                <td colSpan={12} className="muted" style={{ padding: 32, textAlign: 'center' }}>
                  ยังไม่มีบัญชีที่ติดตาม — กดปุ่ม &quot;<b>+ เพิ่มบัญชีติดตาม</b>&quot;
                  ในแท็บตั้งค่าเพื่อเริ่ม
                </td>
              </tr>
            ) : (
              filteredAccounts.map((a) => {
                const items = itemsByAcct.get(a.code) ?? [];
                const r = reconByCode.get(a.code);
                // Use independent GL balance from reconciliation endpoint when available;
                // fall back to item sum (tautological) only when recon data is not yet loaded.
                const glBal = r
                  ? toNumber(r.glBalance)
                  : items.reduce((s, i) => s + toNumber(i.amount), 0);
                const oldItems = r ? r.oldCount : items.filter((i) => i.ageDays > 60).length;
                const avgAge = r
                  ? r.avgAge
                  : items.length
                    ? Math.round(items.reduce((s, i) => s + i.ageDays, 0) / items.length)
                    : 0;
                return (
                  <tr key={a.code} onClick={() => onPick(a)} style={{ cursor: 'pointer' }}>
                    <td onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" />
                    </td>
                    <td className="code">
                      <b>{a.code}</b>
                    </td>
                    <td>
                      <b>{a.name}</b>
                    </td>
                    <td className="muted">{a.type}</td>
                    <td className="muted">{a.module}</td>
                    <td className="muted code" style={{ fontSize: 13 }}>
                      {a.refLabel}
                    </td>
                    <td className="num tnum">
                      <b>{fmt(glBal)}</b>
                    </td>
                    <td className="num">{r ? r.openCount : items.length}</td>
                    <td className="num">
                      {oldItems ? (
                        <span className="neg" style={{ fontWeight: 600 }}>
                          {oldItems}
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="num">
                      {avgAge > 60 ? (
                        <span className="neg" style={{ fontWeight: 600 }}>
                          {avgAge} วัน
                        </span>
                      ) : avgAge > 30 ? (
                        <span className="badge warning" style={{ fontSize: 13 }}>
                          {avgAge} วัน
                        </span>
                      ) : (
                        <span className="muted">{avgAge} วัน</span>
                      )}
                    </td>
                    <td>
                      {r ? (
                        <ReconBadge delta={String(r.delta)} withinTolerance={r.withinTolerance} />
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                    <td>
                      <button
                        className="btn sm"
                        style={{ height: 28, fontSize: 14 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          onPick(a);
                        }}
                      >
                        ดู Schedule →
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
            <tr className="total">
              <td colSpan={6}>รวมทั้งสิ้น</td>
              <td className="num tnum">—</td>
              <td className="num">
                {filteredAccounts.reduce((s, a) => s + (itemsByAcct.get(a.code)?.length ?? 0), 0)}
              </td>
              <td className="num">
                {filteredAccounts.reduce(
                  (s, a) =>
                    s + (itemsByAcct.get(a.code)?.filter((i) => i.ageDays > 60).length ?? 0),
                  0,
                )}
              </td>
              <td colSpan={3} />
            </tr>
          </tbody>
        </table>
      </div>
      <div className="tbl-footer">
        <span>
          คลิกที่บัญชีเพื่อดูรายละเอียดประกอบ — ระบบสร้างให้อัตโนมัติจาก JV ที่ลงในบัญชีนั้น
        </span>
      </div>
    </div>
  );
}

interface ScheduleDetailProps {
  acct: MonAccount;
  accounts: readonly MonAccount[];
  setAcct: (a: MonAccount) => void;
  agingF: string;
  setAgingF: (f: string) => void;
  companyId: string | undefined;
  selectedItemIds: Set<string>;
  setSelectedItemIds: (s: Set<string>) => void;
  onMatch: (ids: string[], notes?: string) => Promise<void>;
  matchPending: boolean;
  asOf?: string;
  activePeriodName: string;
  reconByCode: Map<string, ReconciliationRow>;
}

function GLScheduleDetail({
  acct,
  accounts,
  setAcct,
  agingF,
  setAgingF,
  companyId,
  selectedItemIds,
  setSelectedItemIds,
  onMatch,
  matchPending,
  asOf,
  activePeriodName,
  reconByCode,
}: ScheduleDetailProps) {
  const itemsQuery = useGlScheduleItems(companyId, {
    accountCode: acct.code,
    status: 'OPEN',
    ...(asOf ? { asOf } : {}),
  });
  const rows = itemsQuery.data ?? [];

  // M3 mutations
  const followUpMut = useUpdateGlScheduleFollowUp(companyId);
  const writeOffMut = useWriteOffGlSchedule(companyId);
  const partialMut = usePartialMatchGlSchedule(companyId);
  const [writeOffOpen, setWriteOffOpen] = useState(false);
  const [writeOffError, setWriteOffError] = useState<string | undefined>();

  const ageData = AGING_BUCKETS.map((b) => {
    const bItems = rows.filter((i) => i.ageDays >= b.min && i.ageDays <= b.max);
    return {
      ...b,
      items: bItems,
      amount: bItems.reduce((s, i) => s + toNumber(i.amount), 0),
    };
  });

  const filteredItems =
    agingF === 'all'
      ? rows
      : rows.filter((i) => {
          const b = AGING_BUCKETS.find((x) => x.label === agingF);
          return b ? i.ageDays >= b.min && i.ageDays <= b.max : true;
        });

  const filteredSum = filteredItems.reduce((s, i) => s + toNumber(i.amount), 0);
  // Use the real independent GL balance from the reconciliation endpoint when available.
  // Fall back to the item sum (tautological) only while recon data is loading.
  const reconRow = reconByCode.get(acct.code);
  const glBalance = reconRow
    ? toNumber(reconRow.glBalance)
    : rows.reduce((s, i) => s + toNumber(i.amount), 0);
  const scheduleSum = reconRow
    ? toNumber(reconRow.scheduleOpenSum)
    : rows.reduce((s, i) => s + toNumber(i.amount), 0);
  const reconDelta = reconRow ? toNumber(reconRow.delta) : glBalance - scheduleSum;
  const reconOk = reconRow ? reconRow.withinTolerance : Math.abs(glBalance - scheduleSum) < 0.005;

  // Live sum of currently-checked rows — used as the "balanced before submit"
  // affordance for the Match action.
  const selectedSum = filteredItems
    .filter((r) => selectedItemIds.has(r.id))
    .reduce((s, r) => s + toNumber(r.amount), 0);
  const selectedCount = filteredItems.filter((r) => selectedItemIds.has(r.id)).length;
  const canMatch = selectedCount >= 2 && Math.abs(selectedSum) < 0.005;
  // Write-off: >= 1 selected + sum NOT 0 (residual exists)
  const canWriteOff = selectedItemIds.size >= 1 && Math.abs(selectedSum) >= 0.005;
  // Partial: >= 2 selected + sum NOT 0
  const canPartial = selectedItemIds.size >= 2 && Math.abs(selectedSum) >= 0.005;

  function toggleSelected(id: string) {
    const next = new Set(selectedItemIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedItemIds(next);
  }

  async function onExportSchedule() {
    const cols: ExportColumn<GlScheduleItemListRow>[] = [
      { header: 'Ref', value: (r) => r.refKey ?? '', width: 18 },
      {
        header: 'วันที่',
        value: (r) => (r.voucherDate ? String(r.voucherDate).slice(0, 10) : ''),
        width: 12,
      },
      { header: 'คำอธิบาย', value: (r) => r.description ?? r.party ?? '', width: 40 },
      { header: 'ยอดคงค้าง', value: (r) => Number(r.amount), width: 16 },
      { header: 'อายุ(วัน)', value: (r) => r.ageDays, width: 10 },
      { header: 'JV', value: (r) => r.voucherNo ?? '', width: 16 },
    ];
    await exportRows(
      `ประกอบบัญชี_${acct.code}_${asOf ?? todayStamp()}.xlsx`,
      acct.code,
      cols,
      rows,
    );
  }

  return (
    <>
      <div className="card" style={{ marginBottom: 'var(--section-gap)' }}>
        <div className="card-head">
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <select
              className="select"
              style={{ width: 460, height: 38, fontSize: 14 }}
              value={acct.code}
              onChange={(e) => {
                const next = accounts.find((a) => a.code === e.target.value);
                if (next) setAcct(next);
              }}
            >
              {accounts.map((a) => (
                <option key={a.code} value={a.code}>
                  {a.code} — {a.name}
                </option>
              ))}
            </select>
            <span className="badge brand">{acct.type}</span>
            <span className="meta">
              Reference: <b style={{ color: '#3f3f46' }}>{acct.refLabel}</b> · จาก: {acct.module}
            </span>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button className="btn sm" onClick={() => window.print()}>
              <Icon name="print" size={12} /> พิมพ์
            </button>
            <button className="btn sm" onClick={() => void onExportSchedule()}>
              <Icon name="download" size={12} /> Excel
            </button>
            <button
              className="btn sm"
              title="พิมพ์เป็น PDF ผ่านหน้าต่างพิมพ์"
              onClick={() => window.print()}
            >
              <Icon name="download" size={12} /> PDF ประกอบบัญชี
            </button>
          </div>
        </div>

        <div
          style={{ padding: '18px', background: '#fafafa', borderTop: '1px solid var(--border)' }}
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr auto 1fr auto 1fr',
              gap: 18,
              alignItems: 'center',
            }}
          >
            <div>
              <div className="meta">ยอดคงเหลือใน General Ledger</div>
              <div
                style={{
                  fontSize: 24,
                  fontWeight: 700,
                  marginTop: 4,
                  fontFamily: 'var(--font-display)',
                }}
                className="tnum"
              >
                {fmt(glBalance)}
              </div>
              <div className="meta">
                บัญชี {acct.code}
                {activePeriodName ? ` ณ ${activePeriodName}` : ''}
              </div>
            </div>
            <div
              style={{
                fontSize: 28,
                color: 'var(--text-faint)',
                fontFamily: 'var(--font-display)',
              }}
            >
              =
            </div>
            <div>
              <div className="meta">ผลรวมรายละเอียดประกอบบัญชี</div>
              <div
                style={{
                  fontSize: 24,
                  fontWeight: 700,
                  marginTop: 4,
                  color: 'var(--brand)',
                  fontFamily: 'var(--font-display)',
                }}
                className="tnum"
              >
                {fmt(scheduleSum)}
              </div>
              <div className="meta">รวม {rows.length} Open Items</div>
            </div>
            <div
              style={{
                fontSize: 28,
                color: reconOk ? 'var(--positive)' : 'var(--negative)',
                fontFamily: 'var(--font-display)',
              }}
            >
              {reconOk ? '✓' : '✗'}
            </div>
            <div>
              <div className="meta">ผลต่าง</div>
              <div
                style={{
                  fontSize: 24,
                  fontWeight: 700,
                  marginTop: 4,
                  color: reconOk ? 'var(--positive)' : 'var(--negative)',
                  fontFamily: 'var(--font-display)',
                }}
                className="tnum"
              >
                {fmt(reconDelta)}
              </div>
              <div
                className="meta"
                style={{ color: reconOk ? 'var(--positive)' : 'var(--negative)' }}
              >
                {reconOk ? 'กระทบยอดเรียบร้อย' : 'ผลต่างยังไม่ตรง'}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 'var(--section-gap)' }}>
        <div className="card-head">
          <h3>การกระจายอายุของ Open Items</h3>
          <span className="meta">คลิกเพื่อกรองตารางด้านล่าง</span>
        </div>
        <div className="card-body">
          <div
            style={{
              display: 'flex',
              height: 14,
              borderRadius: 7,
              overflow: 'hidden',
              background: '#f0f0f3',
              marginBottom: 14,
            }}
          >
            {ageData.map((b, i) => {
              const pct = glBalance ? (b.amount / glBalance) * 100 : 0;
              return pct > 0 ? (
                <div
                  key={i}
                  style={{ width: `${pct}%`, background: b.color }}
                  title={`${b.label}: ${fmt(b.amount)}`}
                />
              ) : null;
            })}
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(5,1fr) auto',
              gap: 14,
              alignItems: 'flex-start',
            }}
          >
            {ageData.map((b, i) => (
              <div
                key={i}
                onClick={() => setAgingF(agingF === b.label ? 'all' : b.label)}
                style={{
                  padding: '10px',
                  border: agingF === b.label ? `2px solid ${b.color}` : '1px solid var(--border)',
                  borderRadius: 6,
                  cursor: 'pointer',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                  <span style={{ width: 9, height: 9, borderRadius: 2, background: b.color }} />
                  <span style={{ fontSize: 14, color: 'var(--text-muted)' }}>{b.label}</span>
                </div>
                <div
                  style={{
                    fontSize: 18,
                    fontWeight: 600,
                    marginTop: 4,
                    fontFamily: 'var(--font-display)',
                  }}
                  className="tnum"
                >
                  {fmt(b.amount)}
                </div>
                <div className="meta">{b.items.length} รายการ</div>
              </div>
            ))}
            <button
              className="btn ghost"
              onClick={() => setAgingF('all')}
              style={{ height: '100%' }}
            >
              แสดงทั้งหมด
            </button>
          </div>
        </div>
      </div>

      <div className="tbl-shell">
        <div className="filter-row">
          <span style={{ fontSize: 14, fontWeight: 600 }}>
            รายละเอียดประกอบบัญชี {acct.code} — {acct.name}
          </span>
          {agingF !== 'all' && <span className="chip active">กรอง: {agingF}</span>}
          {selectedCount > 0 && (
            <span className="meta">
              เลือก {selectedCount} รายการ · ผลรวม:{' '}
              <b style={{ color: canMatch ? 'var(--positive)' : 'var(--negative)' }}>
                {fmt(selectedSum)} {canMatch ? '✓' : '✗ (ต้อง=0)'}
              </b>
            </span>
          )}
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
            <button
              className="btn sm primary"
              disabled={!canMatch || matchPending}
              onClick={() => {
                void onMatch(Array.from(selectedItemIds));
              }}
              title={canMatch ? 'จับคู่ปิดรายการที่เลือก' : 'เลือก ≥2 รายการที่รวมกัน = 0'}
            >
              {matchPending ? 'กำลังจับคู่...' : 'จับคู่ปิด (Match)'}
            </button>
            {canWriteOff && (
              <button
                className="btn sm"
                style={{ color: 'var(--warning)' }}
                disabled={writeOffMut.isPending}
                onClick={() => {
                  setWriteOffError(undefined);
                  setWriteOffOpen(true);
                }}
                title="ตัดจำหน่ายส่วนต่างที่อยู่ในวงเงิน Tolerance"
              >
                ตัดจำหน่ายส่วนต่าง (Write-off)
              </button>
            )}
            {canPartial && (
              <button
                className="btn sm"
                disabled={partialMut.isPending || selectedItemIds.size < 2}
                onClick={() => {
                  partialMut.mutate(
                    { itemIds: Array.from(selectedItemIds) },
                    { onSuccess: () => setSelectedItemIds(new Set()) },
                  );
                }}
                title="จับคู่บางส่วน — รายการยังคง OPEN แต่มี Match ID"
              >
                {partialMut.isPending ? 'กำลังจับคู่...' : 'จับคู่บางส่วน (Partial)'}
              </button>
            )}
            <button className="btn sm" onClick={() => void onExportSchedule()}>
              <Icon name="download" size={12} /> Export
            </button>
          </div>
        </div>
        <div className="tbl-wrap" style={{ maxHeight: 540 }}>
          <table className="tbl">
            <thead>
              <tr>
                <th style={{ width: 24 }}>
                  <input type="checkbox" />
                </th>
                <th style={{ width: 60 }}>ลำดับ</th>
                <th style={{ width: 140 }}>Ref ({acct.refLabel})</th>
                <th style={{ width: 100 }}>วันที่</th>
                <th>คำอธิบาย / ผู้ที่เกี่ยวข้อง</th>
                <th>หน่วยงาน</th>
                <th className="num">ยอดคงค้าง</th>
                <th className="num">อายุ (วัน)</th>
                <th>JV อ้างอิง</th>
                <th>โมดูล</th>
                <th>ติดตาม</th>
                <th>การกระทำ</th>
              </tr>
            </thead>
            <tbody>
              {itemsQuery.isPending ? (
                <tr>
                  <td colSpan={12} className="muted" style={{ padding: 24, textAlign: 'center' }}>
                    กำลังโหลด…
                  </td>
                </tr>
              ) : itemsQuery.error ? (
                <tr>
                  <td
                    colSpan={12}
                    className="muted"
                    style={{ padding: 24, textAlign: 'center', color: 'var(--negative)' }}
                  >
                    โหลดล้มเหลว: {itemsQuery.error.message}
                  </td>
                </tr>
              ) : filteredItems.length === 0 ? (
                <tr>
                  <td colSpan={12} className="muted" style={{ padding: 32, textAlign: 'center' }}>
                    ยังไม่มีรายการ Open Items — กด &quot;<b>Sync รายการใหม่</b>&quot; เพื่อดึง JV
                    ล่าสุดเข้า GL Schedule
                  </td>
                </tr>
              ) : (
                filteredItems.map((it, idx) => {
                  const ageBucket =
                    AGING_BUCKETS.find((b) => it.ageDays >= b.min && it.ageDays <= b.max) ??
                    FALLBACK_BUCKET;
                  const amt = toNumber(it.amount);
                  const checked = selectedItemIds.has(it.id);
                  return (
                    <tr
                      key={it.id}
                      style={checked ? { background: 'var(--brand-tint)' } : undefined}
                    >
                      <td>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleSelected(it.id)}
                        />
                      </td>
                      <td className="num">{idx + 1}</td>
                      <td className="code">
                        <a style={{ color: 'var(--brand)', fontWeight: 500 }}>{it.refKey}</a>
                      </td>
                      <td>{it.voucherDate ? fmtDate(it.voucherDate) : '—'}</td>
                      <td>{it.description ?? it.party ?? '—'}</td>
                      <td className="muted">—</td>
                      <td
                        className="num tnum"
                        style={{ color: amt < 0 ? 'var(--negative)' : 'inherit', fontWeight: 500 }}
                      >
                        {fmt(amt)}
                      </td>
                      <td className="num">
                        <span style={{ color: ageBucket.color, fontWeight: 600 }}>
                          {it.ageDays}
                        </span>
                      </td>
                      <td className="code muted" style={{ fontSize: 13 }}>
                        {it.voucherNo ?? '—'}
                      </td>
                      <td className="muted">{acct.module}</td>
                      <td>
                        <FollowUpCell
                          item={it}
                          onChange={(status) =>
                            followUpMut.mutate({ id: it.id, data: { followUpStatus: status } })
                          }
                        />
                      </td>
                      <td>
                        {it.entryId ? (
                          <Link
                            to="/journal"
                            search={{ jv: it.entryId }}
                            className="btn sm ghost"
                            style={{ height: 26, fontSize: 13 }}
                          >
                            ดู JV
                          </Link>
                        ) : (
                          <button
                            className="btn sm ghost"
                            disabled
                            style={{ height: 26, fontSize: 13 }}
                          >
                            ดู JV
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
              <tr className="total">
                <td colSpan={6}>
                  รวมทั้งสิ้น {filteredItems.length} รายการ {agingF !== 'all' && `(กรอง ${agingF})`}
                </td>
                <td className="num tnum">
                  <b>{fmt(filteredSum)}</b>
                </td>
                <td colSpan={5} />
              </tr>
            </tbody>
          </table>
        </div>
        <div className="tbl-footer">
          <span>
            รายงานนี้ได้รับการตรวจสอบกระทบยอดอัตโนมัติแล้ว · พร้อมแนบเป็นเอกสารปิดงวดบัญชี · Export
            PDF ได้
          </span>
        </div>
      </div>

      {writeOffOpen && (
        <WriteOffModal
          residual={selectedSum}
          error={writeOffError}
          onClose={() => setWriteOffOpen(false)}
          onConfirm={(reasonCode) => {
            setWriteOffError(undefined);
            writeOffMut.mutate(
              { itemIds: Array.from(selectedItemIds), reasonCode },
              {
                onSuccess: () => {
                  setWriteOffOpen(false);
                  setSelectedItemIds(new Set());
                },
                onError: (err) => {
                  setWriteOffError(err instanceof Error ? err.message : String(err));
                },
              },
            );
          }}
        />
      )}
    </>
  );
}

// =============================================================================
// AutoClearPreview — dry-run result modal body. Exported for testing.
// =============================================================================
export function AutoClearPreview({
  groups,
  isPending,
  onConfirm,
  onClose,
}: {
  groups: AutoClearResult['groups'];
  isPending: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  return (
    <div>
      {groups.length === 0 ? (
        <div className="muted" style={{ padding: '24px 0', textAlign: 'center' }}>
          ไม่พบกลุ่มที่จับคู่ปิดได้
        </div>
      ) : (
        <>
          <div className="meta" style={{ marginBottom: 12 }}>
            พบ {groups.length} กลุ่มที่ผลรวม = 0 สามารถเคลียร์ได้อัตโนมัติ:
          </div>
          <table className="tbl" style={{ marginBottom: 16 }}>
            <thead>
              <tr>
                <th>บัญชี</th>
                <th>Match Key</th>
                <th className="num">รายการ</th>
                <th className="num">ยอดรวม</th>
              </tr>
            </thead>
            <tbody>
              {groups.map((g, i) => (
                <tr key={i}>
                  <td className="code">{g.accountCode}</td>
                  <td>{g.matchKey}</td>
                  <td className="num">{g.itemIds.length}</td>
                  <td className="num tnum">{fmt(Number(g.total))}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button className="btn ghost" onClick={onClose}>
              ยกเลิก
            </button>
            <button className="btn primary" disabled={isPending} onClick={onConfirm}>
              {isPending ? 'กำลังเคลียร์...' : `ยืนยันเคลียร์ ${groups.length} กลุ่ม`}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// =============================================================================
// ClearingWorklist — open-item rows + matched-group reset. Exported for testing.
// =============================================================================
export function ClearingWorklist({
  openItems,
  matchedItems,
  onReset,
  resetPending,
}: {
  openItems: GlScheduleItemListRow[];
  matchedItems: GlScheduleItemListRow[];
  onReset: (matchId: string) => void;
  resetPending: boolean;
}) {
  // Build a set of matchKeys with opposing signs (potential auto-clearable pairs)
  const matchKeySignMap = useMemo(() => {
    const map = new Map<string, Set<'pos' | 'neg'>>();
    for (const item of openItems) {
      if (!item.matchKey) continue;
      const signs = map.get(item.matchKey) ?? new Set<'pos' | 'neg'>();
      signs.add(Number(item.amount) >= 0 ? 'pos' : 'neg');
      map.set(item.matchKey, signs);
    }
    return map;
  }, [openItems]);

  // Group matched items by matchId
  const matchedGroups = useMemo(() => {
    const groups = new Map<string, GlScheduleItemListRow[]>();
    for (const item of matchedItems) {
      const matchId = item.matchId ?? '';
      const list = groups.get(matchId) ?? [];
      list.push(item);
      groups.set(matchId, list);
    }
    return groups;
  }, [matchedItems]);

  return (
    <>
      {/* Open items worklist */}
      <div className="tbl-shell" style={{ marginBottom: 'var(--section-gap)' }}>
        <div className="filter-row">
          <span style={{ fontSize: 14, fontWeight: 600 }}>
            Open Items รอจับคู่ ({openItems.length} รายการ)
          </span>
        </div>
        <div className="tbl-wrap" style={{ maxHeight: 400 }}>
          <table className="tbl">
            <thead>
              <tr>
                <th>บัญชี</th>
                <th>Match Key</th>
                <th>Ref</th>
                <th>คำอธิบาย</th>
                <th className="num">ยอด</th>
                <th>มีคู่?</th>
              </tr>
            </thead>
            <tbody>
              {openItems.length === 0 ? (
                <tr>
                  <td colSpan={6} className="muted" style={{ padding: 24, textAlign: 'center' }}>
                    ไม่มี Open Items
                  </td>
                </tr>
              ) : (
                openItems.map((item) => {
                  const signs = item.matchKey ? matchKeySignMap.get(item.matchKey) : undefined;
                  const hasPair = signs ? signs.size >= 2 : false;
                  return (
                    <tr key={item.id}>
                      <td className="code">{item.accountCode}</td>
                      <td>{item.matchKey ?? '—'}</td>
                      <td className="code">{item.refKey}</td>
                      <td className="muted">{item.description ?? item.party ?? '—'}</td>
                      <td
                        className="num tnum"
                        style={{
                          color: Number(item.amount) < 0 ? 'var(--negative)' : 'inherit',
                        }}
                      >
                        {fmt(Number(item.amount))}
                      </td>
                      <td>
                        {hasPair ? (
                          <span className="badge positive">มีคู่</span>
                        ) : (
                          <span className="badge">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Matched groups — reset affordance */}
      {matchedGroups.size > 0 && (
        <div className="tbl-shell">
          <div className="filter-row">
            <span style={{ fontSize: 14, fontWeight: 600 }}>
              กลุ่มที่จับคู่แล้ว — ยกเลิกได้ ({matchedGroups.size} กลุ่ม)
            </span>
          </div>
          <div className="tbl-wrap" style={{ maxHeight: 400 }}>
            <table className="tbl">
              <thead>
                <tr>
                  <th>Match ID</th>
                  <th>รายการ</th>
                  <th>การกระทำ</th>
                </tr>
              </thead>
              <tbody>
                {Array.from(matchedGroups.entries()).map(([matchId, items]) => (
                  <tr key={matchId}>
                    <td className="code muted" style={{ fontSize: 12 }}>
                      {matchId.slice(0, 8)}…
                    </td>
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {items.map((it) => (
                          <span key={it.id} className="meta">
                            {it.refKey} · {fmt(Number(it.amount))}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td>
                      <button
                        className="btn sm ghost"
                        disabled={resetPending}
                        onClick={() => onReset(matchId)}
                        aria-label="ยกเลิกการจับคู่"
                      >
                        ยกเลิกการจับคู่
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}

function ClearingPage({ companyId }: { companyId: string | undefined }) {
  const [dryRunResult, setDryRunResult] = useState<AutoClearResult | null>(null);

  const autoClearMut = useAutoClearGlSchedule(companyId);
  const resetMut = useResetGlScheduleMatch(companyId);

  const openItemsQuery = useGlScheduleItems(companyId, { status: 'OPEN' });
  const matchedItemsQuery = useGlScheduleItems(companyId, { status: 'MATCHED' });

  const openItems = openItemsQuery.data ?? [];
  const matchedItems = matchedItemsQuery.data ?? [];

  function handleDryRun() {
    autoClearMut.mutate(
      { dryRun: true },
      {
        onSuccess: (result) => {
          setDryRunResult(result);
        },
      },
    );
  }

  function handleConfirmClear() {
    autoClearMut.mutate(
      { dryRun: false },
      {
        onSuccess: () => {
          setDryRunResult(null);
        },
      },
    );
  }

  return (
    <>
      <div className="card" style={{ marginBottom: 'var(--section-gap)' }}>
        <div className="card-head">
          <div>
            <h3>Manual Clearing — กรณีระบบจับคู่ไม่ได้อัตโนมัติ</h3>
            <div className="sub">
              ส่วนใหญ่ระบบจะจับคู่ Cr ↔ Dr ให้เอง (Reference ตรงกัน) · ใช้ Manual เฉพาะกรณีพิเศษ
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              className="btn primary"
              disabled={autoClearMut.isPending || !companyId}
              onClick={handleDryRun}
              title="ตรวจสอบกลุ่มที่จับคู่ได้ก่อนยืนยัน"
            >
              <Icon name="refresh" size={14} />{' '}
              {autoClearMut.isPending ? 'กำลังตรวจ...' : 'Auto-clear (ตรวจก่อนทำ)'}
            </button>
          </div>
        </div>
        <div className="card-body">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14 }}>
            <div style={{ padding: '12px', background: '#fafafa', borderRadius: 6 }}>
              <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>
                กรณีที่ 1: Partial Clearing
              </div>
              <div className="meta">
                เงินทดรอง 200,000 แต่จ่ายจริง 188,400 → ยอดเหลือ 11,600 ต้องคืน → จับคู่ปิดบางส่วน
              </div>
            </div>
            <div style={{ padding: '12px', background: '#fafafa', borderRadius: 6 }}>
              <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>
                กรณีที่ 2: Cross-reference Match
              </div>
              <div className="meta">
                เคลียร์เงินทดรองหลายใบใน 1 ครั้ง / 1 ใบเคลียร์หลายภารกิจ → จับคู่ N:M
              </div>
            </div>
            <div style={{ padding: '12px', background: '#fafafa', borderRadius: 6 }}>
              <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>
                กรณีที่ 3: Reference พิมพ์ผิด
              </div>
              <div className="meta">
                ผู้บันทึกใส่ Ref ผิด → ใช้ Manual แก้ link ระบบจะ Auto-clear ถ้า ref ตรง
              </div>
            </div>
          </div>
        </div>
      </div>

      <ClearingWorklist
        openItems={openItems}
        matchedItems={matchedItems}
        onReset={(matchId) => resetMut.mutate(matchId)}
        resetPending={resetMut.isPending}
      />

      {dryRunResult !== null && (
        <AppModal
          title="ผลการตรวจสอบ Auto-clear"
          subtitle="รายการกลุ่มที่จับคู่ปิดได้ (ผลรวม = 0)"
          onClose={() => setDryRunResult(null)}
        >
          <AutoClearPreview
            groups={dryRunResult.groups}
            isPending={autoClearMut.isPending}
            onConfirm={handleConfirmClear}
            onClose={() => setDryRunResult(null)}
          />
        </AppModal>
      )}
    </>
  );
}

// =============================================================================
// GLSettingsRow — one row in the Settings table. Exported for unit testing.
// Controlled Active + requireRefKey checkboxes that call onPatch(id, partial).
// =============================================================================
export function GLSettingsRow({
  acct,
  onPatch,
}: {
  acct: MonAccount;
  onPatch: (
    id: string,
    data: {
      isActive?: boolean;
      requireRefKey?: boolean;
      matchKeySource?: GlMatchKeySource;
      autoClear?: boolean;
      toleranceAmount?: string | null;
      tolerancePct?: string | null;
      writeOffAccountCode?: string | null;
    },
  ) => void;
}) {
  return (
    <tr key={acct.code}>
      <td>
        <input type="checkbox" defaultChecked />
      </td>
      <td className="code">
        <b>{acct.code}</b>
      </td>
      <td>{acct.name}</td>
      <td className="muted">{acct.type}</td>
      <td className="muted">{acct.module}</td>
      <td className="code">{acct.refLabel}</td>
      <td>
        <select
          className="select"
          style={{ height: 30, fontSize: 14 }}
          aria-label="Clearing Rule"
          value={acct.matchKeySource}
          onChange={(e) => onPatch(acct.id, { matchKeySource: e.target.value as GlMatchKeySource })}
        >
          <option value="REF_KEY">จับคู่ด้วย Ref อัตโนมัติ</option>
          <option value="PARTY">จับคู่ด้วยคู่สัญญา</option>
          <option value="NONE">ไม่จับคู่อัตโนมัติ</option>
        </select>
      </td>
      <td>
        <input
          type="checkbox"
          aria-label="เปิด Auto-clear อัตโนมัติ"
          checked={acct.autoClear}
          onChange={(e) => onPatch(acct.id, { autoClear: e.target.checked })}
        />
      </td>
      <td>
        <input
          type="checkbox"
          aria-label="บังคับใส่ Ref ตอนลง JV"
          checked={acct.requireRefKey}
          onChange={(e) => onPatch(acct.id, { requireRefKey: e.target.checked })}
        />
      </td>
      <td>
        <input
          type="number"
          className="input"
          aria-label="Tolerance Amount"
          style={{ width: 90, height: 28, fontSize: 13 }}
          defaultValue={acct.toleranceAmount ?? ''}
          placeholder="จำนวนเงิน"
          onBlur={(e) => onPatch(acct.id, { toleranceAmount: e.target.value || null })}
        />
      </td>
      <td>
        <input
          type="number"
          className="input"
          aria-label="Tolerance %"
          style={{ width: 70, height: 28, fontSize: 13 }}
          defaultValue={acct.tolerancePct ?? ''}
          placeholder="%"
          min={0}
          max={100}
          step={0.01}
          onBlur={(e) => onPatch(acct.id, { tolerancePct: e.target.value || null })}
        />
      </td>
      <td>
        <input
          type="text"
          className="input"
          aria-label="Write-off Account Code"
          style={{ width: 110, height: 28, fontSize: 13 }}
          defaultValue={acct.writeOffAccountCode ?? ''}
          placeholder="รหัสตัดจำหน่าย"
          onBlur={(e) => onPatch(acct.id, { writeOffAccountCode: e.target.value || null })}
        />
      </td>
      <td>
        <input
          type="checkbox"
          aria-label="Active"
          checked={acct.isActive}
          onChange={(e) => onPatch(acct.id, { isActive: e.target.checked })}
        />
      </td>
      <td>
        <span className={acct.isActive ? 'badge positive' : 'badge'}>
          {acct.isActive ? 'Active' : 'Inactive'}
        </span>
      </td>
    </tr>
  );
}

function GLSettings({
  accounts,
  companyId,
}: {
  accounts: readonly MonAccount[];
  companyId: string | undefined;
}) {
  const patch = useUpdateGlScheduleAccount(companyId);
  const create = useCreateGlScheduleAccount(companyId);
  const { data: coa = [] } = useCoa(companyId);

  const [showAdd, setShowAdd] = useState(false);
  const [newCode, setNewCode] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [newType, setNewType] = useState<'OPEN_ITEM' | 'RECON_ONLY'>('OPEN_ITEM');

  const coaOptions = useMemo(
    () => coa.map((c) => ({ value: c.code, label: `${c.code} — ${c.nameTh}` })),
    [coa],
  );

  function handleAdd() {
    if (!newCode || !newDesc) return;
    create.mutate(
      {
        accountCode: newCode,
        description: newDesc,
        watchType: newType,
        isActive: true,
        requireRefKey: false,
        matchKeySource: 'REF_KEY',
        autoClear: false,
      },
      {
        onSuccess: () => {
          setShowAdd(false);
          setNewCode('');
          setNewDesc('');
          setNewType('OPEN_ITEM');
        },
      },
    );
  }

  return (
    <>
      <div className="tbl-shell">
        <div className="filter-row">
          <span style={{ fontSize: 14, fontWeight: 600 }}>
            ตั้งค่าบัญชีที่ทำ Open Item Management
          </span>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
            <button className="btn sm primary" onClick={() => setShowAdd(true)}>
              <Icon name="plus" size={12} /> เพิ่มบัญชีติดตาม
            </button>
          </div>
        </div>
        <table className="tbl">
          <thead>
            <tr>
              <th style={{ width: 24 }}>
                <input type="checkbox" />
              </th>
              <th>รหัส</th>
              <th>ชื่อบัญชี</th>
              <th>ประเภท</th>
              <th>Reference Source</th>
              <th>Reference Field</th>
              <th>Clearing Rule</th>
              <th>Auto-clear</th>
              <th>บังคับใส่ Ref ตอนลง JV</th>
              <th>Tolerance (฿)</th>
              <th>Tolerance (%)</th>
              <th>บัญชีตัดจำหน่าย</th>
              <th>Active</th>
              <th>สถานะ</th>
            </tr>
          </thead>
          <tbody>
            {accounts.map((a) => (
              <GLSettingsRow
                key={a.code}
                acct={a}
                onPatch={(id, data) => patch.mutate({ id, data })}
              />
            ))}
          </tbody>
        </table>
        <div
          style={{
            padding: '14px 18px',
            background: '#fafafa',
            fontSize: 14,
            color: 'var(--text-muted)',
            lineHeight: 1.6,
            borderTop: '1px solid var(--border)',
          }}
        >
          <b>คำแนะนำ:</b> เปิด Open Item Management สำหรับบัญชีงบดุล (Balance Sheet) ที่ไม่ใช่
          AR/AP/Cash · บัญชี P&L ไม่ต้องเปิด (ปิดงวดเองทุกปี) ·
          บัญชีรวบยอดอย่างที่ดินอาคารอุปกรณ์ใช้ระบบ Fixed Asset ได้ในตัว · เมื่อเปิดบัญชีใหม่
          ระบบจะบังคับ JV ในบัญชีนั้น<b>ต้องระบุ Reference</b> ก่อน Post (validate at entry) ·
          เปิด/ปิด &quot;บังคับใส่ Ref&quot; ได้ต่อบัญชี
        </div>
      </div>

      {showAdd && (
        <AppModal
          title="เพิ่มบัญชีติดตาม"
          subtitle="เลือกบัญชีจาก COA และระบุรายละเอียด"
          onClose={() => setShowAdd(false)}
          footer={
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn ghost" onClick={() => setShowAdd(false)}>
                ยกเลิก
              </button>
              <button
                className="btn primary"
                onClick={handleAdd}
                disabled={!newCode || !newDesc || create.isPending}
              >
                {create.isPending ? 'กำลังบันทึก...' : 'เพิ่มบัญชี'}
              </button>
            </div>
          }
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: '8px 0' }}>
            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: 14,
                  fontWeight: 500,
                  marginBottom: 6,
                  color: 'var(--text-muted)',
                }}
              >
                รหัสบัญชี (COA)
              </label>
              <SearchSelect
                value={newCode}
                onChange={setNewCode}
                options={coaOptions}
                getLabel={(v) => coaOptions.find((o) => o.value === v)?.label ?? v}
                placeholder="ค้นหาบัญชี..."
              />
            </div>
            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: 14,
                  fontWeight: 500,
                  marginBottom: 6,
                  color: 'var(--text-muted)',
                }}
              >
                คำอธิบาย
              </label>
              <input
                className="input"
                style={{ width: '100%' }}
                value={newDesc}
                onChange={(e) => setNewDesc(e.target.value)}
                placeholder="ระบุคำอธิบายสำหรับบัญชีนี้"
              />
            </div>
            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: 14,
                  fontWeight: 500,
                  marginBottom: 6,
                  color: 'var(--text-muted)',
                }}
              >
                ประเภทการติดตาม
              </label>
              <select
                className="select"
                style={{ width: '100%', height: 38 }}
                value={newType}
                onChange={(e) => setNewType(e.target.value as 'OPEN_ITEM' | 'RECON_ONLY')}
              >
                <option value="OPEN_ITEM">OPEN_ITEM — ติดตาม Open Items</option>
                <option value="RECON_ONLY">RECON_ONLY — กระทบยอดอย่างเดียว</option>
              </select>
            </div>
          </div>
        </AppModal>
      )}
    </>
  );
}
